import { createSlice, createSelector } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";

const epcrHistorySlice = createSlice({
    name: 'epcrHistory',
    initialState: {
        data: {},
        defs: []
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const history of ensureArray(action.payload)) {
                if (!state.data[history.pcrid]) {
                    state.data[history.pcrid] = [history];
                } else {
                    let idx = state.data[history.pcrid].findIndex(h => history.id === h.id);
                    if (idx >= 0) {
                        state.data[history.pcrid].splice(idx, 1, history);
                    } else {
                        state.data[history.pcrid] = [...state.data[history.pcrid], history];
                    }
                }
            }
        },
        remove: (state, action) => {
            for (const history of ensureArray(action.payload)) {
                let idx = state.data[history.pcrid].findIndex(h => h.id === history.id);
                if (idx >= 0) {
                    state.data[history.pcrid].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => epcrHistorySlice.getInitialState());
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(epcrHistorySlice.name);
const fetchEpcrHistory = pcrid => network => network.get('/api/v1/epcrhistory/' + encodeURIComponent(pcrid), epcrHistorySlice.actions.addOrUpdate);
const fetchEpcrHistoryDefs = (network) => network.get('/api/v1/epcrpasthistorydefs', epcrHistorySlice.actions.setDefs);
export const addEpcrHistory = (network, data) => network.put('/api/v1/epcrhistory', data, epcrHistorySlice.actions.addOrUpdate, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
export const removeEpcrHistory = (network, data) => network.delete('/api/v1/epcrhistory/' + encodeURIComponent(data.id), epcrHistorySlice.actions.remove, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
// #endregion

// #region selectors
const selectEpcrHistoryDefs = state => state.epcrHistory.defs;
const selectEpcrHistories = state => state.epcrHistory.data;

const eagerSelector = createEagerSelector(epcrHistorySlice.name, { mode: 'mapToArray' });
const selectEpcrHistoryByID = eagerSelector(createSelector([selectEpcrHistories, (histories, pcrid) => pcrid], (histories, pcrid) => histories[pcrid] ?? []));
const selectEpcrHistory = pcrid  => state => selectEpcrHistoryByID(state, pcrid);
// #endregion

// #region listeners
const listen = {
    epcrHistoryAdded: (history, { dispatch }) => dispatch(epcrHistorySlice.actions.addOrUpdate(history)),
    epcrHistoryRemoved: (history, { dispatch }) => dispatch(epcrHistorySlice.actions.remove(history))
}
// #endregion 

export const [useEpcrHistory, realizeEpcrHistory] = dataHookFactory(selectEpcrHistory, initMapFactory(fetchEpcrHistory, listen, pcrid => pcrid?.toString()));
export const [useEpcrHistoryDefs, realizeEpcrHistoryDefs] = dataHookFactory(selectEpcrHistoryDefs, { run: fetchEpcrHistoryDefs });

const reducerDetails = { name: epcrHistorySlice.name, reducer: epcrHistorySlice.reducer };
export default reducerDetails;